import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  FiAlertCircle,
  FiCheck,
  FiClock,
  FiMail,
  FiPaperclip,
  FiSend,
  FiShield,
  FiUser,
} from 'react-icons/fi';

const initialFormData = {
  name: '',
  email: '',
  problem: '',
};

const MAX_ATTACHMENT_SIZE = 5 * 1024 * 1024;

const getSupportApiUrl = () => {
  if (process.env.REACT_APP_SUPPORT_API_URL) {
    return process.env.REACT_APP_SUPPORT_API_URL;
  }

  if (process.env.NODE_ENV === 'development') {
    return 'http://localhost:4000/api/support';
  }

  return '/api/support';
};

const SupportPage = () => {
  const [formData, setFormData] = useState(initialFormData);
  const [attachment, setAttachment] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [submitMessage, setSubmitMessage] = useState('');

  const apiUrl = getSupportApiUrl();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleAttachmentChange = (e) => {
    const file = e.target.files?.[0];

    if (!file) {
      setAttachment(null);
      return;
    }

    if (file.size > MAX_ATTACHMENT_SIZE) {
      setSubmitStatus('error');
      setSubmitMessage('Please choose an attachment smaller than 5MB.');
      e.target.value = '';
      setAttachment(null);
      return;
    }

    setSubmitStatus(null);
    setSubmitMessage('');
    setAttachment(file);
  };

  const readAttachment = (file) =>
    new Promise((resolve, reject) => {
      if (!file) {
        resolve(null);
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result || '';
        const content = String(result).split(',')[1];
        resolve({
          content,
          filename: file.name,
          type: file.type || 'application/octet-stream',
        });
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    setSubmitMessage('');

    try {
      const encodedAttachment = await readAttachment(attachment);
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          attachment: encodedAttachment,
        }),
      });

      const responseText = await response.text();
      const result = responseText
        ? JSON.parse(responseText)
        : {};

      if (!response.ok) {
        throw new Error(result.message || 'We could not send your support request.');
      }

      setSubmitStatus('success');
      setSubmitMessage('Your support request has been sent. We will reply as soon as possible.');
      setFormData(initialFormData);
      setAttachment(null);
      e.target.reset();
    } catch (error) {
      setSubmitStatus('error');
      setSubmitMessage(
        error instanceof SyntaxError
          ? 'The support API route is not connected. Please start the support API or proxy /api/support to it.'
          : error.message || 'Something went wrong. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="min-h-screen pt-28">
      <section className="section-padding bg-gradient-to-b from-blue-50 via-white to-gray-50">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-14"
          >
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-100 mb-4">
              <span className="text-sm font-medium text-blue-700">
                Support Center
              </span>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              How can we help?
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Share the details of the issue and attach a screenshot or file if it helps us understand the problem faster.
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-[0.85fr_1.15fr] gap-12 items-start">
            <motion.aside
              initial={{ opacity: 0, x: -40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
              className="space-y-6"
            >
              <div className="glass-effect rounded-2xl p-8 shadow-lg">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                  What happens next
                </h2>
                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
                      <FiMail className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">Ticket email sent</h3>
                      <p className="text-gray-600">Your request is delivered to the support inbox using Twilio SendGrid.</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center">
                      <FiClock className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">Fast response</h3>
                      <p className="text-gray-600">The team reviews the issue details and replies to the email you provide.</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                      <FiShield className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">Private by default</h3>
                      <p className="text-gray-600">Attachments are sent directly with the support request and are not stored in the browser.</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.aside>

            <motion.div
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="glass-effect rounded-2xl p-8 shadow-xl"
            >
              {submitStatus && (
                <motion.div
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`mb-6 p-4 rounded-xl ${
                    submitStatus === 'success'
                      ? 'bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200'
                      : 'bg-gradient-to-r from-red-50 to-pink-50 border border-red-200'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <div className={`w-8 h-8 rounded-full ${
                      submitStatus === 'success'
                        ? 'bg-gradient-to-r from-green-500 to-emerald-500'
                        : 'bg-gradient-to-r from-red-500 to-pink-500'
                    } flex items-center justify-center`}>
                      {submitStatus === 'success' ? (
                        <FiCheck className="w-4 h-4 text-white" />
                      ) : (
                        <FiAlertCircle className="w-4 h-4 text-white" />
                      )}
                    </div>
                    <div>
                      <h3 className={`font-semibold ${submitStatus === 'success' ? 'text-green-800' : 'text-red-800'}`}>
                        {submitStatus === 'success' ? 'Request Sent' : 'Request Failed'}
                      </h3>
                      <p className={`text-sm ${submitStatus === 'success' ? 'text-green-600' : 'text-red-600'}`}>
                        {submitMessage}
                      </p>
                    </div>
                  </div>
                </motion.div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Name *
                    </label>
                    <div className="relative">
                      <FiUser className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="text"
                        name="name"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        className="w-full pl-12 pr-4 py-3 rounded-xl border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition"
                        placeholder="Your name"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email *
                    </label>
                    <div className="relative">
                      <FiMail className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full pl-12 pr-4 py-3 rounded-xl border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition"
                        placeholder="you@example.com"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Problem *
                  </label>
                  <textarea
                    name="problem"
                    required
                    value={formData.problem}
                    onChange={handleChange}
                    rows="7"
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition resize-none"
                    placeholder="Tell us what happened, what you expected, and any steps we can use to reproduce it."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Attachment
                  </label>
                  <label className="flex min-h-[76px] cursor-pointer items-center justify-between gap-4 rounded-xl border border-dashed border-gray-300 bg-white px-4 py-3 transition hover:border-blue-400 hover:bg-blue-50">
                    <div className="flex items-center gap-3">
                      <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
                        <FiPaperclip className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">
                          {attachment ? attachment.name : 'Upload a file'}
                        </div>
                        <div className="text-sm text-gray-500">
                          PNG, JPG, PDF, or document up to 5MB
                        </div>
                      </div>
                    </div>
                    <span className="text-sm font-semibold text-blue-600">Choose</span>
                    <input
                      type="file"
                      name="attachment"
                      onChange={handleAttachmentChange}
                      className="sr-only"
                    />
                  </label>
                </div>

                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full flex items-center justify-center gap-3 rounded-xl px-6 py-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold tracking-wide shadow-lg shadow-indigo-500/30 hover:from-indigo-500 hover:to-purple-500 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:ring-offset-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Sending...</span>
                    </>
                  ) : (
                    <>
                      <FiSend className="w-5 h-5" />
                      <span>Send Support Request</span>
                    </>
                  )}
                </motion.button>
              </form>
            </motion.div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default SupportPage;
